import { useContext, createContext, useState, useEffect, useMemo } from "react"

import { useQueryClient } from "@tanstack/react-query"

// api
import {
  usePostForceWorkflow,
  usePostCancelWorkflow,
  usePostHoldWorkflow,
  usePostUnholdWorkflow,
} from "api/hooks/workflows/useWorkflowsApi"

export const WorkflowContext = createContext({})

export const WorkflowProvider = ({ children }) => {
  const queryClient = useQueryClient()

  /** LOCAL STATE (could pass as props) */
  const [workflowId, setWorkflowId] = useState(null)
  const [workflowModel, setWorkflowModel] = useState(null)
  const [selectedAction, setSelectedAction] = useState(null)
  const [openForceModal, setOpenForceModal] = useState(null)
  const [openEditStatesModal, setOpenEditStatesPanel] = useState(false)
  const [openCancelWorkflowModal, setOpenCancelWorkflowModal] = useState(false)
  const [parentQueries, setParentQueries] = useState([])

  // parentQueries set by Activity and Request pages
  const invalidateParentQueries = () => {
    if (parentQueries) {
      parentQueries.forEach((query) => {
        queryClient.invalidateQueries({ queryKey: query })
      })
    }
  }

  /** API MUTATE FUNCTIONS (could localize) */
  const forceWorkflow = usePostForceWorkflow({ workflowId, workflowModel })
  const cancelWorkflow = usePostCancelWorkflow({ workflowId, workflowModel })
  const holdWorkflow = usePostHoldWorkflow({ workflowId, workflowModel })
  const unholdWorkflow = usePostUnholdWorkflow({ workflowId, workflowModel })

  /** TODO: SERVER STATE - do not save server state into local state */
  const [workflowTrackerData, setWorkflowTrackerData] = useState({})

  /** TODO: make following derived (from server) state into pure functions or just localize */

  const isWorkflowCancelled = useMemo(() => {
    return workflowTrackerData?.workflow_data?.is_cancelled
  }, [workflowTrackerData?.workflow_data?.is_cancelled])

  const isOnHold = useMemo(() => {
    return workflowTrackerData?.workflow_data?.is_onhold
  }, [workflowTrackerData?.workflow_data?.is_onhold])

  const isWorkflowComplete = useMemo(() => {
    if (workflowTrackerData?.state_data?.tracker_data) {
      const td = workflowTrackerData?.state_data?.tracker_data
      return td[td.length - 1]?.is_current ? true : false
    }
  }, [workflowTrackerData?.state_data])

  const trackerData = useMemo(() => {
    return workflowTrackerData?.state_data?.tracker_data || []
  }, [workflowTrackerData?.state_data?.tracker_data])

  const currentState = useMemo(() => {
    if (workflowTrackerData?.state_data?.tracker_data) {
      const curr = workflowTrackerData?.state_data?.tracker_data.find((state) => state.is_current)
      if (curr) return curr
    }
    return null
  }, [workflowTrackerData?.state_data?.tracker_data])

  const projectedCompletionDate = useMemo(() => {
    if (workflowTrackerData?.state_data?.tracker_data) {
      const tracker = workflowTrackerData.state_data.tracker_data
      let lastState = tracker[tracker.length - 1]
      return lastState.start_state_date
    }
    return null
  }, [workflowTrackerData?.state_data?.tracker_data])

  const completedStates = useMemo(() => {
    if (workflowTrackerData?.state_data?.tracker_data) {
      return workflowTrackerData.state_data.tracker_data.filter((state) => state.status === "Completed")
    }
    return []
  }, [workflowTrackerData?.state_data?.tracker_data])

  return (
    <WorkflowContext.Provider
      value={{
        /** LOCAL STATE (could pass as props) */

        // workflow ID
        workflowId, // EditWorkflowModal, PizzaTrackerContainer
        setWorkflowId, // Activity, Request, Activities

        // workflow model
        workflowModel, // CancelWorkflowModal, EditWorkflowModal, ForceStepModal
        setWorkflowModel, // Activity, Request, Activities

        // selected action
        selectedAction, // ForceStepModal
        setSelectedAction, // Activity

        // force modal state
        openForceModal, // ForceStepModal
        setOpenForceModal, // ForceStepModal, TrackerHeader

        // edit state panel state
        openEditStatesModal, // EditWorkflowModal
        setOpenEditStatesPanel, // EditWorkflowModal, TrackerHeader

        // cancel workflow modal state
        setOpenCancelWorkflowModal, // CancelWorkflowModal, TrackerHeader
        openCancelWorkflowModal, // CancelWorkflowModal

        // parent queries
        setParentQueries, // Activity, Request
        invalidateParentQueries, // CancelWorkflowModal, EditWorkflowModal, ForceStepModal, TrackerHeader

        /** API MUTATE FUNCTIONS (could localize) */

        forceWorkflow, // ForceStepModal
        cancelWorkflow, // CancelWorkflowModal
        holdWorkflow, // TrackerHeader
        unholdWorkflow, // ForceStepModal

        /** TODO: SERVER STATE - do not save server state into local state; remove from context provider and localize */

        workflowTrackerData, // WorkflowTracker, EditWorkflowModal, ForceStepModal, PizzaTrackerContainer, Activities
        setWorkflowTrackerData, // WorkflowTracker, PizzaTrackerContainer, Activities

        /** TODO: DERIVED STATE - make following derived (from server) state into pure functions or just localize */

        isWorkflowCancelled, // State (WorkflowTracker/Tracker), TrackerHeader, TrackerStatus
        isOnHold, // EditWorkflowModal, State (WorkflowTracker/Tracker), TrackerHeader
        isWorkflowComplete, // WorkflowTracker, EditWorkflowModal, State (WorkflowTracker/Tracker), TrackerHeader, TrackerStatus, PizzaTrackerContainer
        trackerData, // ForceStepModal, TrackerStatus
        currentState, // ForceStepModal, TrackerHeader
        projectedCompletionDate, // useRequestApi, RequestOverview
        completedStates, // TrackerHeader
      }}
    >
      {children}
    </WorkflowContext.Provider>
  )
}

const useWorkflowTracker = () => {
  return useContext(WorkflowContext)
}

export default useWorkflowTracker
